import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip,
  Line, Scatter, ReferenceLine,
} from "recharts";
import { Activity, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { API, useAuth } from "@/App";

const fmt = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const UnifiedFertilityChart = () => {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ovulations, setOvulations] = useState([]);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const days = 28;
        const since = new Date();
        since.setDate(since.getDate() - days + 1);
        const sinceStr = since.toISOString().slice(0, 10);

        const [bbtRes, opkRes, intRes, ovRes] = await Promise.all([
          axios.get(`${API}/wellness/health-data?type=temperature&days=${days}`, { headers, withCredentials: true }),
          axios.get(`${API}/wellness/ttc/opk/history?days=${days}`, { headers, withCredentials: true }),
          axios.get(`${API}/wellness/ttc/intimacy/history?days=${days}`, { headers, withCredentials: true }),
          axios.get(`${API}/wellness/ovulation/history?limit=12`, { headers, withCredentials: true }),
        ]);
        const bbt = bbtRes.data?.samples || [];
        const opk = opkRes.data?.entries || [];
        const intimacy = intRes.data?.entries || [];
        const ovs = ovRes.data?.dates || [];

        // Build a daily row covering the window, indexed by date
        const dayMap = {};
        for (let i = 0; i < days; i++) {
          const d = new Date(since);
          d.setDate(since.getDate() + i);
          const k = d.toISOString().slice(0, 10);
          dayMap[k] = { date: k, bbt: null, opk: null, intimacy: null };
        }
        bbt.forEach((s) => { if (dayMap[s.date]) dayMap[s.date].bbt = s.value; });
        opk.forEach((o) => {
          if (dayMap[o.date]) {
            dayMap[o.date].opk = o.result === "peak" ? 1 : o.result === "positive" ? 0.66 : 0.33;
            dayMap[o.date].opk_result = o.result;
          }
        });
        intimacy.forEach((e) => { if (dayMap[e.date]) dayMap[e.date].intimacy = 0.15; });

        setData(Object.values(dayMap).filter((r) => r.date >= sinceStr));
        setOvulations(ovs.filter((d) => d >= sinceStr));
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, [token]);

  if (loading) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="p-10 flex justify-center">
          <Loader2 className="w-6 h-6 text-pink-300 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const hasAny = data.some((d) => d.bbt || d.opk || d.intimacy) || ovulations.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="unified-fertility-chart"
    >
      <Card className="glass border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-300" />
            Fertility Chart · 28 days
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!hasAny ? (
            <p className="text-mystic-300 text-sm py-10 text-center">
              Start logging BBT, OPK, intimacy, or ovulation marks to see your unified chart appear.
            </p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmt} stroke="#a8a4c4" tick={{ fontSize: 11 }} interval={Math.floor(data.length / 7)} />
                  <YAxis yAxisId="bbt" domain={[96, 100]} stroke="#fda4af" tick={{ fontSize: 11 }} width={36} />
                  <YAxis yAxisId="event" orientation="right" hide domain={[0, 1.1]} />
                  <Tooltip
                    contentStyle={{ background: "#1a0f2e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }}
                    labelStyle={{ color: "#fff" }}
                    formatter={(value, name, p) => {
                      if (name === "BBT") return [`${value} °F`, name];
                      if (name === "OPK") return [p.payload.opk_result || "—", name];
                      if (name === "Intimacy") return ["♥", name];
                      return [value, name];
                    }}
                    labelFormatter={fmt}
                  />
                  <Line yAxisId="bbt" type="monotone" dataKey="bbt" name="BBT" stroke="#fda4af" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  <Scatter yAxisId="event" dataKey="opk" name="OPK" fill="#fcd34d" />
                  <Scatter yAxisId="event" dataKey="intimacy" name="Intimacy" fill="#f43f5e" shape="diamond" />
                  {ovulations.map((d) => (
                    <ReferenceLine key={d} yAxisId="bbt" x={d} stroke="#34d399" strokeDasharray="4 4" label={{ value: "Ov", fill: "#34d399", fontSize: 10, position: "top" }} />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 mt-3 text-xs text-mystic-300">
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-rose-300" /> BBT</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-300" /> OPK</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rotate-45 bg-rose-500" /> Intimacy</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 border-t-2 border-dashed border-emerald-400" /> Ovulation mark</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default UnifiedFertilityChart;
